import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { api_url_v1 } from '../utils/Config';
import api from '../utils/Api';


// Async Thunks for Skills
export const fetchSkills = createAsyncThunk(
  'attributes/fetchSkills',
  async (_, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.get(`${api_url_v1}/skills`,{
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addSkill = createAsyncThunk(
  'attributes/addSkill',
  async (skill, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.post(`${api_url_v1}/skills`, skill,{
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateSkill = createAsyncThunk(
  'attributes/updateSkill',
  async ({ id, ...updates }, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.put(`${api_url_v1}/skills/${id}`, updates,{
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteSkill = createAsyncThunk(
  'attributes/deleteSkill',
  async (id, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      await api.delete(`${api_url_v1}/skills/${id}`,{
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

// Async Thunks for Interests
export const fetchInterests = createAsyncThunk(
  'attributes/fetchInterests',
  async (_, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.get(`${api_url_v1}/interests`,{
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const addInterest = createAsyncThunk(
  'attributes/addInterest',
  async (interest, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.post(`${api_url_v1}/interests`, interest,{
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const updateInterest = createAsyncThunk(
  'attributes/updateInterest',
  async ({ id, ...updates }, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      const response = await api.put(`${api_url_v1}/interests/${id}`, updates,{
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return response.data.data;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

export const deleteInterest = createAsyncThunk(
  'attributes/deleteInterest',
  async (id, { getState, rejectWithValue }) => {
    const { auth: { token } } = getState();
    try {
      await api.delete(`${api_url_v1}/interests/${id}`,{
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        }
      });
      return id;
    } catch (error) {
      return rejectWithValue(error.response.data);
    }
  }
);

const initialState = {
  skills: {
    items: [],
    isLoading: false,
    error: null,
    lastUpdated: null,
  },
  interests: {
    items: [],
    isLoading: false,
    error: null,
    lastUpdated: null,
  }
};

const attributesSlice = createSlice({
  name: 'attributes',
  initialState,
  reducers: {
    clearErrors: (state) => {
      state.skills.error = null;
      state.interests.error = null;
    },
    resetState: () => initialState,
  },
  extraReducers: (builder) => {
    // Skills reducers
    builder
      .addCase(fetchSkills.pending, (state) => {
        state.skills.isLoading = true;
        state.skills.error = null;
      })
      .addCase(fetchSkills.fulfilled, (state, action) => {
        state.skills.items = action.payload;
        state.skills.isLoading = false;
        state.skills.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchSkills.rejected, (state, action) => {
        state.skills.isLoading = false;
        state.skills.error = action.payload || 'Failed to fetch skills';
      })
      .addCase(addSkill.fulfilled, (state, action) => {
        state.skills.items.push(action.payload);
        state.skills.lastUpdated = new Date().toISOString();
      })
      .addCase(updateSkill.fulfilled, (state, action) => {
        const index = state.skills.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.skills.items[index] = action.payload;
        }
        state.skills.lastUpdated = new Date().toISOString();
      })
      .addCase(deleteSkill.fulfilled, (state, action) => {
        state.skills.items = state.skills.items.filter(item => item.id !== action.payload);
        state.skills.lastUpdated = new Date().toISOString();
      })

      // Interests reducers
      .addCase(fetchInterests.pending, (state) => {
        state.interests.isLoading = true;
        state.interests.error = null;
      })
      .addCase(fetchInterests.fulfilled, (state, action) => {
        state.interests.items = action.payload;
        state.interests.isLoading = false;
        state.interests.lastUpdated = new Date().toISOString();
      })
      .addCase(fetchInterests.rejected, (state, action) => {
        state.interests.isLoading = false;
        state.interests.error = action.payload || 'Failed to fetch interests';
      })
      .addCase(addInterest.fulfilled, (state, action) => {
        state.interests.items.push(action.payload);
        state.interests.lastUpdated = new Date().toISOString();
      })
      .addCase(updateInterest.fulfilled, (state, action) => {
        const index = state.interests.items.findIndex(item => item.id === action.payload.id);
        if (index !== -1) {
          state.interests.items[index] = action.payload;
        }
        state.interests.lastUpdated = new Date().toISOString();
      })
      .addCase(deleteInterest.fulfilled, (state, action) => {
        state.interests.items = state.interests.items.filter(item => item.id !== action.payload);
        state.interests.lastUpdated = new Date().toISOString();
      });
  },
});

export const { clearErrors, resetState } = attributesSlice.actions;

// Selectors
export const selectSkills = (state) => state.attributes.skills.items;
export const selectSkillsLoading = (state) => state.attributes.skills.isLoading;
export const selectSkillsError = (state) => state.attributes.skills.error;

export const selectInterests = (state) => state.attributes.interests.items;
export const selectInterestsLoading = (state) => state.attributes.interests.isLoading;
export const selectInterestsError = (state) => state.attributes.interests.error;

export default attributesSlice.reducer;